import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from './AuthContext';
import {
  ROUTE_TO_PAGE_MAP,
  PAGE_PRIORITY,
  normalizePaginaList,
} from '../constants/pageRoutes';

const PermissionsContext = createContext(null);

function getUserPermissaoId(user) {
  if (!user) return null;

  if (user.permissao?.id != null) {
    const id = Number(user.permissao.id);
    if (!Number.isNaN(id)) return id;
  }

  if (user.permissaos_id != null && user.permissaos_id !== '') {
    const id = Number(user.permissaos_id);
    if (!Number.isNaN(id)) return id;
  }

  return null;
}

function buildRoleFromPages(permissaosId, paginaList, meta = {}) {
  return {
    id: meta.role_id ?? meta.id ?? 0,
    nome: meta.role_nome ?? meta.nome ?? 'Role',
    descricao: meta.descricao ?? '',
    permissaos_id: permissaosId,
    paginaList: normalizePaginaList(paginaList),
    users: [],
  };
}

function syncUserPaginaList(user, paginaList) {
  try {
    const next = {
      ...user,
      paginaList: normalizePaginaList(paginaList),
    };
    localStorage.setItem('user', JSON.stringify(next));
  } catch {
    // ignore
  }
}

function getErrorMessage(error) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return String(error);
}

function extractPaginaListFromUnknown(payload) {
  if (!payload || typeof payload !== 'object') return [];
  return normalizePaginaList(
    payload.paginaList ?? payload.pagina_list ?? payload.paginas ?? payload.pages
  );
}

export function PermissionsProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [allowedPages, setAllowedPages] = useState([]);
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const applyAccess = useCallback((role) => {
    if (!role) {
      setUserRole(null);
      setAllowedPages([]);
      return;
    }
    const pages = normalizePaginaList(role.paginaList);
    setUserRole({ ...role, paginaList: pages });
    setAllowedPages(pages);
  }, []);

  const loadAccess = useCallback(async () => {
    if (!user) {
      applyAccess(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const permissaoId = getUserPermissaoId(user);
    const cachedPages = normalizePaginaList(user.paginaList);

    if (cachedPages.length > 0 && permissaoId != null) {
      applyAccess(buildRoleFromPages(permissaoId, cachedPages));
    }

    let resolved = false;

    // 1) Endpoint dedicado (não exige admin)
    try {
      const response = await apiService.getMyAccessPages();
      const payload = response?.data || response || {};
      const pages = extractPaginaListFromUnknown(payload);
      const pid =
        payload.permissaos_id != null
          ? Number(payload.permissaos_id)
          : permissaoId;

      if (pages.length > 0 || pid != null) {
        applyAccess(
          buildRoleFromPages(pid, pages, {
            role_id: payload.role_id,
            role_nome: payload.role_nome,
          })
        );
        syncUserPaginaList(user, pages);
        resolved = pages.length > 0;
      }
    } catch (error) {
      console.warn('access/pages indisponível:', getErrorMessage(error));
    }

    // 2) Profile com paginaList (após deploy da API)
    if (!resolved) {
      try {
        const profile = await apiService.getProfile();
        const pages = extractPaginaListFromUnknown(profile);
        const pid = getUserPermissaoId(profile) ?? permissaoId;

        if (pages.length > 0 && pid != null) {
          applyAccess(buildRoleFromPages(pid, pages));
          syncUserPaginaList(user, pages);
          resolved = true;
        }
      } catch (error) {
        console.warn('profile para ACL falhou:', getErrorMessage(error));
      }
    }

    // 3) Cache do login
    if (!resolved) {
      if (cachedPages.length > 0 && permissaoId != null) {
        applyAccess(buildRoleFromPages(permissaoId, cachedPages));
      } else if (permissaoId != null) {
        applyAccess(buildRoleFromPages(permissaoId, []));
        console.warn(
          'Nenhuma página carregada para permissaos_id:',
          permissaoId,
          '— publique a API com GET /access/pages.'
        );
      } else {
        applyAccess(null);
        console.warn('Usuário sem permissaos_id; ACL indisponível');
      }
    }

    setLoading(false);
  }, [user, applyAccess]);

  useEffect(() => {
    if (authLoading) return;
    loadAccess();
  }, [authLoading, loadAccess]);

  useEffect(() => {
    if (loading && user && !authLoading) {
      const timeout = setTimeout(() => {
        console.warn('Timeout ao carregar permissões. Forçando loading = false.');
        setLoading(false);
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [loading, user, authLoading]);

  const hasAccessToPage = useCallback(
    (pageName) => {
      if (pageName === 'profile') return true;
      if (!userRole) return false;
      const pages = Array.isArray(userRole.paginaList) ? userRole.paginaList : [];
      return pages.includes(pageName);
    },
    [userRole]
  );

  const hasAccessToCurrentRoute = useCallback(() => {
    const currentPage = ROUTE_TO_PAGE_MAP[location.pathname];
    if (!currentPage) return true;
    return hasAccessToPage(currentPage);
  }, [location.pathname, hasAccessToPage]);

  const getFirstAllowedPage = useCallback(() => {
    if (!userRole || !allowedPages.length) {
      console.warn('Nenhuma página permitida encontrada. Redirecionando para /profile');
      return '/profile';
    }

    for (const page of PAGE_PRIORITY) {
      if (allowedPages.includes(page)) {
        const route = Object.keys(ROUTE_TO_PAGE_MAP).find(
          (key) => ROUTE_TO_PAGE_MAP[key] === page
        );
        if (route) return route;
      }
    }

    if (allowedPages.length > 0) {
      const firstAllowedPage = allowedPages[0];
      const route = Object.keys(ROUTE_TO_PAGE_MAP).find(
        (key) => ROUTE_TO_PAGE_MAP[key] === firstAllowedPage
      );
      if (route) return route;
    }

    return '/profile';
  }, [userRole, allowedPages]);

  const value = useMemo(
    () => ({
      roles: [],
      userRole,
      allowedPages,
      loading: loading || authLoading,
      hasAccessToPage,
      hasAccessToCurrentRoute,
      getFirstAllowedPage,
      loadRoles: loadAccess,
      reload: loadAccess,
    }),
    [
      userRole,
      allowedPages,
      loading,
      authLoading,
      hasAccessToPage,
      hasAccessToCurrentRoute,
      getFirstAllowedPage,
      loadAccess,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de PermissionsProvider');
  }
  return context;
}
